/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';
import Retracked from 'js/app/retracked';
import path from 'js/lib/path';

import { breakpoints } from '@coursera/cds-core';

import { CourseraMetatags } from 'bundles/seo';
import HeroBanner from 'bundles/shared-articles/components/ArticleHubHeroBanner';
import HubPageGoogleSchemaMarkup from 'bundles/shared-articles/components/seo/HubPageGoogleSchemaMarkup';
import useArticleUrlData from 'bundles/shared-articles/hooks/useArticleUrlData';
import CannedCollections from 'bundles/unified-seo-content-common/components/hub-page/CannedCollections';
import CategoryCollection from 'bundles/unified-seo-content-common/components/hub-page/CategoryCollection';
import CategoryNavigation from 'bundles/unified-seo-content-common/components/hub-page/CategoryNavigation';
import Layout from 'bundles/unified-seo-content-common/components/hub-page/Layout';
import RichText from 'bundles/unified-seo-content-common/components/rich-text/RichText';
import PageDataErrorHandler from 'bundles/unified-seo-content-common/components/shared/PageDataErrorHandler';
import HubPageSkeleton from 'bundles/unified-seo-content-common/components/skeletons/HubPageSkeleton';
import { PAGE_TYPE, SEO_CONTENT_PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import { useSeoContentMainHubPageQuery } from 'bundles/unified-seo-content-common/hooks/useSeoContentMainHubPageQuery';
import { getPageLink } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/articles';

const styles = {
  rowsContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-800);
    ${breakpoints.down('md')} {
      gap: var(--cds-spacing-400);
    }
  `,
  cannedCollectionContainer: css`
    margin: 0 auto;
  `,
  fullWidthContainer: css`
    width: calc(100% + var(--cds-spacing-600));
    border-radius: var(--cds-spacing-100);
    background-color: var(--cds-color-neutral-background-primary-weak);
    padding: var(--cds-spacing-300);
    margin-left: calc(var(--cds-spacing-300) * -1);
  `,
};

export const HubPage = () => {
  const { isPreview, subfolderLocaleCode } = useArticleUrlData();

  const {
    title,
    introduction,
    featuredImageUrl,
    featuredImageAlt,
    featuredContent,
    bottomContentJson,
    bottomContentLinks,
    featuredContentByCategoryList,
    isHubPageDataLoading,
    navigationLinks,
  } = useSeoContentMainHubPageQuery({
    pageType: SEO_CONTENT_PAGE_TYPE.articles,
    preview: isPreview,
    subfolderLocaleCode,
  });

  const missingRequiredFields =
    !featuredContentByCategoryList || !title || !introduction || !featuredImageUrl || !featuredContent;

  if (isHubPageDataLoading) {
    return <HubPageSkeleton />;
  }

  if (missingRequiredFields) {
    return <PageDataErrorHandler />;
  }

  const metaTitle = _t('Coursera Career Education Hub');
  const metaDescription = _t('Find career education resources, career development, tips, insights and more.');

  const hubPageUrl = path.join(config.url.domain, '/articles');

  return (
    <React.Fragment>
      <Layout
        heroBannerComponent={
          <HeroBanner
            title={title}
            subtitle={introduction}
            image={{
              src: featuredImageUrl,
              alt: featuredImageAlt || _t('Coursera Articles Hero Banner'),
            }}
          />
        }
        navigationComponent={<CategoryNavigation activeCategorySlug="articles" navigationLinks={navigationLinks} />}
        contentComponent={
          <div css={styles.rowsContainer}>
            <CategoryCollection
              contentItems={featuredContent}
              isLoading={isHubPageDataLoading}
              imageLazyLoad={false}
              isFeaturedContentCollection
            />
            {featuredContentByCategoryList[0]?.title && (
              <CategoryCollection
                collectionHeaderText={featuredContentByCategoryList[0].title}
                viewAllCTA={getPageLink({
                  slug: featuredContentByCategoryList[0].slug ?? '',
                  localizedCountryCode: featuredContentByCategoryList[0].countryCode,
                  pageType: PAGE_TYPE.categoryHub,
                })}
                contentItems={featuredContentByCategoryList[0].featuredContentCollection?.items}
                isLoading={isHubPageDataLoading}
                imageLazyLoad={true}
              />
            )}
            <div css={styles.fullWidthContainer}>
              <div css={styles.cannedCollectionContainer}>
                <CannedCollections />
              </div>
            </div>
            {featuredContentByCategoryList.slice(1)?.map(
              (featuredContentByCategory) =>
                featuredContentByCategory?.title && (
                  <CategoryCollection
                    key={featuredContentByCategory.title}
                    collectionHeaderText={featuredContentByCategory.title}
                    viewAllCTA={getPageLink({
                      slug: featuredContentByCategory.slug ?? '',
                      localizedCountryCode: featuredContentByCategory.countryCode,
                      pageType: PAGE_TYPE.categoryHub,
                    })}
                    contentItems={featuredContentByCategory.featuredContentCollection?.items}
                    isLoading={isHubPageDataLoading}
                    imageLazyLoad={true}
                  />
                )
            )}
            {bottomContentJson && <RichText content={bottomContentJson} links={bottomContentLinks} />}
          </div>
        }
      />
      <CourseraMetatags
        metaNameAndProperties={{
          title: metaTitle,
          ogAndTwitterTitle: metaTitle,
          description: metaDescription,
          disableCourseraDescriptionSuffix: true,
        }}
        hrefLangOverrides={() => []}
      />
      <HubPageGoogleSchemaMarkup
        hubPageUrl={hubPageUrl}
        hubPageName={_t('Coursera Articles')}
        hubBreadcrumbList={[{ name: _t('Articles'), absoluteUrl: hubPageUrl }]}
      />
    </React.Fragment>
  );
};

export default Retracked.createTrackedContainer(() => ({
  namespace: {
    app: 'articles',
    page: 'article_hub',
  },
}))(HubPage);
